import { useAuth } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { Route, Redirect, useLocation } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

const ProtectedRoute = ({ children, path }) => {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const location = useLocation();
  const [authStatus, setAuthStatus] = useState("loading"); // "loading", "authorized", "unauthorized", "onboarding", "no_subscription", "inactive_subscription"

  useEffect(() => {
    const verifyUser = async () => {
      if (!isLoaded) return; // Wait for Clerk Auth to fully load

      if (!isSignedIn) {
        setAuthStatus("unauthorized");
        return;
      }

      try {
        const token = await getToken({ template: "default" });

        // Check user details first (includes subscription status)
        const userDetailsRes = await fetch(`${BASE_URL}/user/details`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!userDetailsRes.ok) {
          throw new Error("Failed to fetch user details");
        }

        const userDetails = await userDetailsRes.json();
        
        // Check if subscription is inactive
        const isInactive = userDetails.subscription_status === 'INACTIVE' || userDetails.subscription_status === 'inactive';
        
        // Allowed pages when subscription is inactive (account settings, billing, contact support)
        const allowedInactivePages = ['/dashboard', '/profile', '/billing', '/support', '/pricing'];
        const currentPath = location.pathname;
        
        if (isInactive) {
          if (!allowedInactivePages.includes(currentPath)) {
            // Redirect to dashboard if trying to access restricted page
            setAuthStatus("redirect_to_dashboard");
            return;
          } else {
            // Allow access to allowed pages when inactive (skip dealership check)
            setAuthStatus("authorized");
            return;
          }
        }

        // Only check dealership status if subscription is active
        const res = await fetch(`${BASE_URL}/user/dealership-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const json = await res.json();
        const hasDealership = json.has_dealership;

        // If user lacks dealership
        if (!hasDealership) {
          if (location.pathname === "/onboarding") {
            setAuthStatus("authorized");
          } else {
            setAuthStatus("onboarding");
          }
        } else {
          // User has dealership - don't allow access to onboarding
          if (location.pathname === "/onboarding") {
            setAuthStatus("redirect_from_onboarding");
          } else {
            setAuthStatus("authorized");
          }
        }
      } catch (err) {
        console.error("Error verifying user:", err);
        setAuthStatus("unauthorized");
      }
    };

    verifyUser();
    // Re-run when auth state or pathname changes to catch onboarding completion
  }, [isSignedIn, isLoaded, location.pathname]);

  // Handle redirects - send to app sign-in page, not Clerk hosted
  if (authStatus === "loading") return null;
  if (authStatus === "unauthorized") {
    return <Redirect to={{ pathname: "/sign-in", state: { from: location } }} />;
  }
  if (authStatus === "no_subscription") {
    return <Redirect to="/pricing" />;
  }
  if (authStatus === "redirect_to_dashboard") {
    return <Redirect to="/dashboard" />;
  }
  if (authStatus === "redirect_from_onboarding") {
    return <Redirect to="/dashboard" />;
  }
  if (authStatus === "onboarding" && location.pathname !== "/onboarding") {
    return <Redirect to="/onboarding?step=signup" />;
  }

  return <Route path={path}>{children}</Route>;
};

export default ProtectedRoute;
